import { useEffect, useRef } from 'react';
import { cn } from 'src/utils/className';
import { TranscriptMessage } from 'src/utils/transcript';

export type TranscriptDisplayProps = {
  messages: TranscriptMessage[];
  onMessageClick?: (index: number) => void;
  shouldHighlightMessage?: (index: number) => boolean;
  autoScroll?: boolean;
  className?: string;
  emptyStateMessage?: string;
  showTimestamp?: boolean;
  rawTranscript?: string;
};

export const TranscriptDisplay = ({
  messages,
  onMessageClick,
  shouldHighlightMessage,
  autoScroll,
  className = 'flex-1',
  emptyStateMessage = 'No transcript available.',
  showTimestamp = true,
  rawTranscript,
}: TranscriptDisplayProps) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesContainerRef.current && autoScroll) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, autoScroll]);

  if (messages.length === 0) {
    if (rawTranscript) {
      return (
        <div
          className={cn(
            'overflow-y-auto border border-neutral-200 rounded-lg p-4 bg-neutral-50',
            className,
          )}
        >
          <div className="text-neutral-500 italic">{rawTranscript}</div>
        </div>
      );
    }

    if (emptyStateMessage) {
      return (
        <div
          className={cn(
            'border border-neutral-200 rounded-lg p-4 bg-neutral-50 flex items-center justify-center',
            className,
          )}
        >
          <p className="text-neutral-400">{emptyStateMessage}</p>
        </div>
      );
    }

    return null;
  }

  return (
    <div
      ref={messagesContainerRef}
      className={cn(
        'overflow-y-auto border border-neutral-200 rounded-lg p-4 bg-neutral-50',
        className,
      )}
    >
      <div className="space-y-4">
        {messages.map((msg, index) => {
          const isHuman = msg.speaker === 'HUMAN';
          const isHighlighted = shouldHighlightMessage?.(index);

          return (
            <div
              key={index}
              id={`message-${index}`}
              className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}
            >
              <div
                onClick={() => onMessageClick?.(index)}
                className={cn(
                  `max-w-[80%] rounded-lg px-4 py-2 transition-opacity ${
                    onMessageClick
                      ? 'cursor-pointer hover:opacity-80'
                      : 'cursor-default'
                  } ${
                    isHuman
                      ? 'bg-primary text-neutral-0'
                      : 'bg-neutral-0 text-neutral-800 border border-neutral-200 hover:opacity-60'
                  }`,
                  isHighlighted ? 'border-2 shadow-lg border-gold-300' : '',
                )}
              >
                {showTimestamp && (msg.timestamp || msg.speaker) && (
                  <div className="text-xs opacity-70 mb-1">
                    {msg.timestamp ? `${msg.timestamp} - ` : ''}
                    {msg.speaker}
                  </div>
                )}
                <div className="whitespace-pre-wrap">{msg.message}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
